import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { FileText, Clock, CheckCircle, XCircle, AlertCircle, Send, CalendarDays, Edit, Lock, CreditCard, Trash2, Loader2 } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const API_URL = "";

const statusMap = {
  submitted: { label: 'Submitted', color: 'bg-blue-100 text-blue-800', icon: Clock },
  accepted: { label: 'Accepted', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  rejected: { label: 'Not Selected', color: 'bg-red-100 text-red-800', icon: XCircle },
  waitlisted: { label: 'Waitlisted', color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle },
  pending_payment: { label: 'Payment Pending', color: 'bg-amber-100 text-amber-800', icon: CreditCard }
};

export const MemberSubmissions = () => {
  const { token, user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [mySubmissions, setMySubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null); // submission id pending confirmation
  const [deleting, setDeleting] = useState(false);

  const headers = { 'Authorization': `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      const [callsRes, subsRes] = await Promise.all([
        fetch(`${API_URL}/api/exhibitions/calls/open`, { headers }),
        fetch(`${API_URL}/api/submissions/my`, { headers })
      ]);
      
      if (callsRes.ok) setCalls(await callsRes.json());
      if (subsRes.ok) setMySubmissions(await subsRes.json());
    } catch (e) {
      toast.error('Failed to load submissions data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/members/login');
      return;
    }
    if (token) fetchData();
  }, [authLoading, isAuthenticated, token, fetchData, navigate]);

  // Check which calls the member has already submitted to
  // Check which calls the member has already submitted to (exclude pending_payment)
  const submittedCallIds = new Set(mySubmissions.filter(s => s.status !== 'pending_payment').map(s => s.call_id));
  const pendingPaymentSubs = mySubmissions.filter(s => s.status === 'pending_payment');

  // Delete helpers
  const getDeleteBlockedReason = (sub) => {
    if (sub.is_locked_override) return 'Admin locked';
    if (sub.payment_status === 'paid') return "Paid submissions can't be deleted";
    if (sub.is_locked) return 'Locked after deadline';
    return null;
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_URL}/api/submissions/${deleteTarget}`, {
        method: 'DELETE',
        headers
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to delete submission');
        return;
      }
      setMySubmissions(prev => prev.filter(s => s.id !== deleteTarget));
      toast.success('Submission deleted.');
    } catch (e) {
      toast.error('Failed to delete submission');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cafa-red"></div>
      </div>
    );
  }

  const isActiveMember = user?.active_member;

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white" data-testid="member-submissions-page">
      <SEOHead title="Submissions | The Coppini Academy of Fine Art" />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-serif text-stone-900 mb-3">Call for Submissions</h1>
          <p className="text-lg text-stone-600">Submit your artwork for upcoming exhibitions</p>
        </div>

        {!isActiveMember && (
          <Card className="mb-8 border-amber-300 bg-amber-50" data-testid="inactive-member-notice">
            <CardContent className="py-6 text-center">
              <AlertCircle size={32} className="mx-auto mb-3 text-amber-600" />
              <p className="text-lg font-medium text-amber-800">Active Membership Required</p>
              <p className="text-amber-700 mt-2">Only active members can submit artwork. Please renew your membership to participate.</p>
              <Link to="/membership"><Button className="mt-4 bg-cafa-red hover:bg-cafa-red-900 text-white">View Membership Options</Button></Link>
            </CardContent>
          </Card>
        )}

        {/* Open Calls */}
        <section className="mb-12">
          <h2 className="text-2xl font-serif text-stone-900 mb-6">Open Calls</h2>
          
          {calls.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText size={48} className="mx-auto mb-4 text-stone-300" />
                <p className="text-lg text-stone-500">No open calls at this time.</p>
                <p className="text-stone-400 mt-2">Check back soon for upcoming exhibitions.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {calls.map(call => {
                const alreadySubmitted = submittedCallIds.has(call.id);
                const deadline = new Date(call.deadline);
                const daysLeft = Math.ceil((deadline - new Date()) / (1000 * 60 * 60 * 24));
                
                return (
                  <Card key={call.id} className="border-stone-200 shadow-md hover:shadow-lg transition-shadow" data-testid={`call-card-${call.id}`}>
                    <CardContent className="p-6 sm:p-8">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="text-xl sm:text-2xl font-serif text-stone-900">{call.exhibition?.title || call.title}</h3>
                          <p className="text-stone-600 mt-2">{call.title}</p>
                          
                          {call.instructions && (
                            <p className="text-stone-500 mt-3 text-sm leading-relaxed">{call.instructions}</p>
                          )}
                          
                          <div className="flex flex-wrap items-center gap-4 mt-4">
                            <div className="flex items-center gap-2 text-stone-700">
                              <CalendarDays size={18} className="text-cafa-red" />
                              <span className="text-base font-medium">
                                Deadline: {deadline.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                              </span>
                            </div>
                            {daysLeft > 0 && daysLeft <= 14 && (
                              <Badge className="bg-amber-100 text-amber-800 text-sm px-3 py-1">{daysLeft} day{daysLeft !== 1 ? 's' : ''} left</Badge>
                            )}
                          </div>
                          
                          <div className="flex flex-wrap gap-3 text-sm text-stone-500 mt-3">
                            <span>Up to {call.max_artworks} artworks</span>
                            {call.fees_enabled && <span>Fee: ${call.submission_fee + call.per_artwork_fee}/artwork</span>}
                          </div>
                        </div>
                        
                        <div className="sm:text-right">
                          {alreadySubmitted ? (
                            <Badge className="bg-green-100 text-green-800 text-base px-4 py-2">Submitted</Badge>
                          ) : pendingPaymentSubs.find(s => s.call_id === call.id) ? (
                            <Link to={`/members/submissions/${call.id}`}>
                              <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white text-lg px-8 py-6 h-auto" data-testid={`pay-btn-${call.id}`}>
                                <CreditCard size={20} className="mr-2" /> Complete Payment
                              </Button>
                            </Link>
                          ) : isActiveMember ? (
                            <Link to={`/members/submissions/${call.id}`}>
                              <Button size="lg" className="bg-cafa-red hover:bg-cafa-red-900 text-white text-lg px-8 py-6 h-auto" data-testid={`submit-btn-${call.id}`}>
                                <Send size={20} className="mr-2" /> Submit Artwork
                              </Button>
                            </Link>
                          ) : (
                            <Button size="lg" disabled className="text-lg px-8 py-6 h-auto opacity-50">Membership Required</Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </section>

        {/* My Submissions */}
        {mySubmissions.length > 0 && (
          <section>
            <h2 className="text-2xl font-serif text-stone-900 mb-6">My Submissions</h2>
            <TooltipProvider>
              <div className="space-y-3">
                {mySubmissions.map(sub => {
                  const s = statusMap[sub.status] || statusMap.submitted;
                  const Icon = s.icon;
                  const blockedReason = getDeleteBlockedReason(sub);
                  const canDelete = !blockedReason;
                  return (
                    <Card key={sub.id} data-testid={`my-submission-${sub.id}`}>
                      <CardContent className="py-4 flex items-center justify-between flex-wrap gap-3">
                        <div>
                          <p className="font-semibold text-stone-900">{sub.exhibition_title}</p>
                          <p className="text-sm text-stone-500 mt-1">
                            {sub.total_artworks} artwork{sub.total_artworks !== 1 ? 's' : ''} &bull; Submitted {new Date(sub.submitted_at).toLocaleDateString()}
                            {sub.updated_at && sub.updated_at !== sub.submitted_at && (
                              <span className="text-stone-400"> &bull; Updated {new Date(sub.updated_at).toLocaleDateString()}</span>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {sub.is_locked ? (
                            <Badge className="bg-stone-100 text-stone-600 gap-1 text-sm px-3 py-1" data-testid={`locked-badge-${sub.id}`}>
                              <Lock size={12} /> Locked
                            </Badge>
                          ) : sub.is_editable && sub.status === 'submitted' ? (
                            <Link to={`/members/submissions/${sub.call_id}?edit=${sub.id}`}>
                              <Button variant="outline" size="sm" data-testid={`edit-btn-${sub.id}`}>
                                <Edit size={14} className="mr-1" /> Edit Submission
                              </Button>
                            </Link>
                          ) : null}

                          {/* Delete Button */}
                          {canDelete ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-400"
                              onClick={() => setDeleteTarget(sub.id)}
                              data-testid={`delete-btn-${sub.id}`}
                            >
                              <Trash2 size={14} className="mr-1" /> Delete
                            </Button>
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    disabled
                                    className="border-stone-200 text-stone-400 cursor-not-allowed opacity-60"
                                    data-testid={`delete-btn-disabled-${sub.id}`}
                                  >
                                    <Trash2 size={14} className="mr-1" /> Delete
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                <p>{blockedReason}</p>
                              </TooltipContent>
                            </Tooltip>
                          )}

                          <Badge className={`${s.color} gap-1 text-sm px-3 py-1`}><Icon size={14} />{s.label}</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TooltipProvider>
          </section>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteTarget} onOpenChange={open => { if (!open) setDeleteTarget(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-xl font-serif">Delete this submission?</AlertDialogTitle>
              <AlertDialogDescription className="text-base text-stone-600">
                This cannot be undone. Your artwork details and images will be permanently removed.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="text-base" disabled={deleting} data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700 text-white text-base"
                onClick={handleDeleteConfirm}
                disabled={deleting}
                data-testid="delete-confirm-btn"
              >
                {deleting ? <><Loader2 size={16} className="mr-2 animate-spin" /> Deleting...</> : 'Yes, Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default MemberSubmissions;
